import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';

import { updateOrg } from '../action/org/update-org';
import { loadOrgJobs } from '../job/actions/load-org-jobs';
import { loadOrg } from '../organization/actions/load-org';
import { userSelector } from '../reducers/current-user-selectors';
import { jobsForOrgIdSelector } from '../reducers/job-selectors';
import { orgSelector, orgStatusSelector } from '../reducers/org-selectors';

import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';

import { HIRING_PERMISSION } from '../util/permissions';
import { StateAutocomplete } from '../widgets/state-autocomplete';

const handleFormChange =
  (setFunction: React.Dispatch<React.SetStateAction<string>>) =>
  (e: React.ChangeEvent<any>, newValue?: any) => {
    const { value } = e.target;
    setFunction(newValue || value);
  };

const getCityAndStateIfAllMatch = (jobs: Array<Job>) => {
  let city: string | null = null;
  let state: string | null = null;
  let hasMismatch = false;
  if (jobs.length === 0) {
    return null;
  }
  jobs.forEach((job) => {
    if (!city) {
      ({ city } = job);
    }
    if (!state) {
      ({ state } = job);
    }

    if (job.city !== city) {
      hasMismatch = true;
    }
    if (job.state !== state) {
      hasMismatch = true;
    }
  });

  if (hasMismatch) {
    return null;
  }

  return {
    city,
    state,
  };
};

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  cityStateProps: {
    org: orgSelector(state, orgId),
    orgState: orgStatusSelector(state, orgId),
    jobs: jobsForOrgIdSelector(state, orgId),
    user: userSelector(state),
  },
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  /*
  DP - 5/15/2020
  This is important so that it doesn't collide with a sub-component
  prop name
   */
  hocActions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    loadOrgJobs: () => dispatch(loadOrgJobs(orgId)),
    updateOrganization: (update: Partial<Org>) => dispatch(updateOrg(orgId, update)),
  },
});

type InputProps = {
  orgId: number;
  onClose: () => void;
};
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateCityOptionalStepProviderProps = InputProps & StateProps & DispatchProps;

export const stateCityOptionalStepProvider = <P extends object>(
  phoneNumberFunction: (org: Org) => string | null,
  WrappedComponent: React.ComponentType<P>,
) =>
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )((props: StateCityOptionalStepProviderProps) => {
    const { hocActions, cityStateProps, ...passedInProps } = props;
    const { org, orgState, user, jobs } = cityStateProps;

    const [newOrgCity, setNewOrgCity] = useState<string>('');
    const [newOrgState, setNewOrgState] = useState<string | null>(null);
    const hasHiringAccess = user.hiringPermission === HIRING_PERMISSION.ALL;

    useEffect(() => {
      hocActions.loadOrg();
      if (org && !phoneNumberFunction(org) && hasHiringAccess) {
        hocActions.loadOrgJobs();
      }
    }, [passedInProps.orgId, org, hocActions, hasHiringAccess]);

    if (!org || orgState?.loading) {
      return null;
    }

    const { city, state } = org;

    // If we already have the relevant phone number or location, then
    // just skip the provider
    if (phoneNumberFunction(org) || (city && state)) {
      return <WrappedComponent {...(passedInProps as P)} />;
    }

    // If we have permissions to jobs, check to see if
    // the jobs all match. If they do, set that to be the
    // org location.
    if (jobs && (!city || !state)) {
      const matchingCityAndState = getCityAndStateIfAllMatch(jobs);
      if (matchingCityAndState) {
        hocActions.updateOrganization(matchingCityAndState);
        return <WrappedComponent {...(passedInProps as P)} />;
      }
    }

    // And if we're waiting for jobs to load, then return null
    if (!jobs && !phoneNumberFunction(org) && hasHiringAccess) {
      return null;
    }

    // If none of the above is true, show the city, state selectors

    const handleSaveOrganizationLocation = () => {
      if (newOrgCity && newOrgState) {
        hocActions.updateOrganization({
          city: newOrgCity,
          state: newOrgState,
        });
      }
    };

    return (
      <UiDialog size="sm" onClose={passedInProps.onClose} title="Local Phone Number">
        <UiDialog.Content>
          <div>
            <div className="typo-title">
              Let us know where your headquarters is so we can use a local phone number.
            </div>
            <div className="u-display-flex u-margin-top-md">
              <div className="u-width-50 u-margin-right">
                <FormControl fullWidth>
                  <InputLabel margin="dense">City</InputLabel>
                  <Input value={newOrgCity} onChange={handleFormChange(setNewOrgCity)} />
                </FormControl>
              </div>
              <div className="u-width-50">
                <StateAutocomplete onChange={setNewOrgState} value={newOrgState || null} />
              </div>
            </div>
          </div>
        </UiDialog.Content>
        <UiDialog.Actions>
          <UiButton onClick={handleSaveOrganizationLocation}>Save</UiButton>
        </UiDialog.Actions>
      </UiDialog>
    );
  });
